import { CodeMapSet, CodeMapSetService, CodeMapType } from "@app/codemaps";
import { County } from "@app/geography";
import { ClientInsuranceModel, InsuranceService } from "@app/insurance";
import { MemberSetModel, MemberSetService } from "@app/members";
import { ProviderSet } from "@app/provider-sets";
import { RegionSet } from "@app/region-sets";
import LookupRegionSet from "@app/region-sets/editor/lookupRegionSet";
import { AccessCriteriaGranularityEnum, AccuracyTemplateService, TemplateService, TemplateSpecialtyGroup } from "@app/templates";
import { Roles, UserContext, UserContextProps, withUserContext } from "@app/user";
import difference from "lodash/difference";
import some from "lodash/some";
import sortBy from "lodash/sortBy";
import * as React from "react";
import { Route, RouteComponentProps, Switch, generatePath } from "react-router-dom";
import CreateProject from "@app/projects/create/createProject";
import { CountyStatus, CreateProjectWizardState, SpecialtyOverrides } from "@app/projects/create/interfaces";

import { SpecialtyDefinition, SpecialtyDefinitionService } from "@app/specialty-definitions";
import {
  CreateProjectStepMembers,
  CreateProjectStepNetworkTaxonomy,
  CreateProjectStepOptions,
  CreateProjectStepProviders,
  CreateProjectStepReview,
  CreateProjectStepServiceArea,
  CreateProjectStepSetup,
  CreateProjectStepTemplate,
} from ".";
import CreateProjectOverrideMemberSet from "@app/projects/createProjectOverrideMemberSet";
import { ProjectOverrideMemberSetType, RegionSetStatusType, TimeDistanceCalculatorType } from "@app/projects/enumerators";
import { MemberSetTypeEnum } from "@app/members/enumerations";
import Features from "@app/feature-flags/features";
import { NetworkTaxonomyData } from "@app/network-taxonomies/networkTaxonomy";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { ResourceType } from "@app/resources";
import { createProjectThingsToResetIfZipProject, createThingsToResetIfZipProject } from "@app/projects/helpers";
import { ResourceTagModel } from "@app/tags/import/types";

type UpdateResourceKeys = keyof CreateProject;

type CreateProjectWizardProps = UserContextProps & RouteComponentProps & FeatureFlagContextProps;

class CreateProjectWizard extends React.Component<CreateProjectWizardProps, CreateProjectWizardState> {
  private templateService = new TemplateService();
  private accuracyTemplateService = new AccuracyTemplateService();
  private memberSetService = new MemberSetService();
  private codeMapSetService = new CodeMapSetService();
  private specialtyDefinitionService = new SpecialtyDefinitionService();
  private insuranceService = new InsuranceService();

  constructor(props: CreateProjectWizardProps) {
    super(props);

    const initialProgressList =
      ["Initial Setup", "Template", "Members", "Providers", "Network Taxonomy", "Service Area", "Options", "Confirm",];

    const hideStrictTimeDistance = this.props.enabledFeatureFlags.has(Features.HIDE_STRICT_TIME_DISTANCE);
    const hideStandardSettingsCreation = !(this.props.enabledFeatureFlags.has(Features.STANDARD_SETTING_CREATION) && this.props.user.roles.includes(Roles.STANDARDS_CREATOR_USER));
    const showNetworkSpecialtyCodeMap = this.props.enabledFeatureFlags.has(Features.NETWORK_BENCHMARKING) && this.props.user.roles.includes(Roles.COMPETITIVE_INSIGHTS_USER);

    this.state = {
      accessCodeMapDefaultSetId: null,
      accuracyIsLoading: false,
      accuracySpecialtyDefinitions: [],
      accuracyTemplateCollections: [],
      allAccessCodeMapSets: [],
      allMemberPopulations: [],
      alternateMemberships: [],
      authorizedInsurances: [],
      authorizedInsurancesErred: false,
      baseUrl: this.props.match.url,
      createProjectModel: {
        accessCodeMapSetDescription: null,
        accessCodeMapSetId: null,
        accuracyAndAdequacy: false,
        accuracySpecialtyDefinitionDesc: null,
        accuracySpecialtyDefinitionId: null,
        accuracySpecialtyToQdpCodeMapSetDescription: null,
        accuracySpecialtyToQdpCodeMapSetId: null,
        accuracyTemplateCollectionDescription: "",
        accuracyTemplateCollectionId: null,
        accuracyTemplateCorrelationId: "",
        adequacyTemplateCollectionDescription: "",
        adequacyTemplateCollectionId: null,
        adequacyTemplateCorrelationId: "",
        autoArchiveUnarchiveSpecialtyExceptions: false,
        calculateStandards: false,
        countyStatuses: [],
        createProjectOverrideMemberSets: this.populateOverride(),
        description: "",
        displayOnNetworkAccordion: false,
        enableRegulatoryExceptionReports: false,
        excludeDeactivatedFacilitiesImpact: false,
        excludeDeactivatedFacilitiesNetwork: false,
        excludeDeactivatedProvidersImpact: false,
        excludeDeactivatedProvidersNetwork: false,
        excludeInVolumeActions: false,
        excludeInvalidNppesNpiFacilitiesImpact: false,
        excludeInvalidNppesNpiFacilitiesNetwork: false,
        excludeInvalidNppesNpiProvidersImpact: false,
        excludeInvalidNppesNpiProvidersNetwork: false,
        excludeMedicareOptOutProvidersImpact: false,
        excludeMedicareOptOutProvidersNetwork: false,
        excludeMissingNpiFacilitiesImpact: false,
        excludeMissingNpiFacilitiesNetwork: false,
        excludeMissingNpiProvidersImpact: false,
        excludeMissingNpiProvidersNetwork: false,
        excludeOIGExcludedProvidersImpact: false,
        excludeOIGExcludedProvidersNetwork: false,
        excludePrecludedProvidersImpact: false,
        excludePrecludedProvidersNetwork: false,
        excludedSpecialtyGroupCodes: [],
        hasZipSummaries: false,
        impactExclusions: ["None"],
        includeGapAging: false,
        includeInEnterpriseComplianceDashboard: false,
        inspectedInsurancesList: [],
        isPrivate: false,
        marketProviderDescription: "",
        marketProviderSetId: null,
        marketSpecialtyCodeMapSetId: null,
        memberPopDescription: "",
        memberPopDescription2: "",
        memberSetAlias: "",
        memberSetAlias2: "",
        memberSetId: null,
        memberSetId2: null,
        membershipRiskMemberSetAlias: "",
        membershipRiskMemberSetId: null,
        name: "",
        networkExclusions: ["None"],
        networkProviderDescription: "",
        networkProviderSetId: null,
        networkSpecialtyCodeMapSetId: null,
        prospectProviderDescription: "",
        prospectProviderSetId: null,
        regulatoryDomainId: null,
        serviceAreaRegionSetId: null,
        showAccuracyGapAnalysis: false,
        showAdvancedComparison: false,
        showAffiliationsInsights: false,
        showAttestation: false,
        showAverageDistance: false,
        showAverageTime: false,
        showCMSCredit: false,
        showCareJourneyScores: false,
        showClosestProviderReport: false,
        showLocationConfidenceScores: false,
        showMaximumDistance: false,
        showMaximumTime: false,
        showMipsScore: false,
        showMotiveAppropriatenessScore: false,
        showNextClosestNetworkProviders: false,
        showNppesDates: false,
        showPatternOfCareProviders: false,
        showPciDashboard: false,
        showPciData: false,
        showTricareClaimsVolumeColumn: false,
        showVaClaimsVolumeColumn: false,
        strictTimeAndDistance: false,
        tags: [
          {
            tagGroupName: "",
            tagName: "",
          }
        ],
        telehealthSpecialtyGroupCodes: [],
        timeDistanceCalculatorType: TimeDistanceCalculatorType.Quest,
        timeDistanceCalculatorTypes: [],
        useMarketProvidersForImpact: true,
        useMarketProvidersForMarketAnalysis: false,
        useNetworkProvidersForMarketAnalysis: false,
        useProspectProvidersForMarketAnalysis: false,
      },
      disableMemberBindingTable: true,
      hideCMSApplicationCreditOption: true,
      hideCareJourneyScores: true,
      hideClosestProviderReportOption: false,
      hideEnterpriseComplianceDashboard: true,
      hideMotiveAppropriatenessScore: true,
      hideShowInMarketAnalysis: true,
      hideStandardsCreation: hideStandardSettingsCreation,
      hideStrictTimeDistance: hideStrictTimeDistance,
      isNetworkTaxonomyValid: true,
      isZipProjectTemplate: false,
      lookupRegionSet: null,
      networkProviderSetNetworkTaxonomy: null,
      originalTemplateCollections: [],
      progressList: initialProgressList,
      projectAccessCodeMapSets: [],
      projectCollections: [],
      projectMemberPopulations: [],
      projectTemplateCollections: [],
      selectedInsuranceUid: "",
      selectedInsurances: [],
      serviceAreaRegionSet: null,
      showNetworkSpecialtyCodeMap: showNetworkSpecialtyCodeMap,
      specialtyCodeMapSets: [],
      specialtyDefinitionsMissing: false,
      specialtyGroups: [],
      specialtyOverrides: [],
      specialtyToQdpCodeMapSets: [],
      timeDistanceCalculatorTypes: [],
      triggerClose: null,
      updateCounties: [],
      useAccuracy: false,
      useAdequacy: true,
    };
  }

  public componentDidUpdate(prevProps: CreateProjectWizardProps) {
    if (prevProps.enabledFeatureFlags !== this.props.enabledFeatureFlags) {
      const updatedProgressList =
        ["Initial Setup", "Template", "Members", "Providers", "Network Taxonomy", "Service Area", "Options", "Confirm",];
      this.setState({
        progressList: updatedProgressList,
      });
    }
  }

  public render() {
    const accuracyTemplateComplete = (!!this.state.createProjectModel.accuracyTemplateCollectionId &&
      !!this.state.createProjectModel.accuracyTemplateCorrelationId &&
      this.state.createProjectModel.accuracyTemplateCorrelationId !== "");
    const adequacyTemplateComplete = (!!this.state.createProjectModel.adequacyTemplateCollectionId &&
      !!this.state.createProjectModel.adequacyTemplateCorrelationId &&
      this.state.createProjectModel.adequacyTemplateCorrelationId !== "" &&
      (this.state.projectAccessCodeMapSets &&
        (this.state.projectAccessCodeMapSets.length > 0 &&
          this.state.createProjectModel.accessCodeMapSetId !== null) ||
        this.state.projectAccessCodeMapSets.length === 0));
    const completionTracker: boolean[] = [];

    
    const steps: { (props: RouteComponentProps, index: number): React.ReactNode; }[] = [];
    steps.push((props, index) =>
      <CreateProjectStepSetup
        {...props}
        createProjectModel={this.state.createProjectModel}
        baseUrl={this.state.baseUrl}
        progressList={this.state.progressList}
        onNextClick={() => { this.handleRenderTemplateCollectionsList(); this.handleRenderTemplatesList(); this.handleRenderMemberPopList(); this.handleGatherCodeMapSets(); }}
        handleNameChange={this.handleNameChange}
        handleDescChange={this.handleDescChange}
        handleTaggingChange={this.handleTaggingChange}
        handleIsPrivateChange={this.handleIsPrivateChange}
        currentIndex={index}
      />);
    completionTracker.push(this.state.createProjectModel.name !== "" && this.state.createProjectModel.description !== "")

    steps.push((props, index) =>
      <UserContext.Consumer>
        {(user) => (
          <CreateProjectStepTemplate
            {...props}
            accuracyIsLoading={this.state.accuracyIsLoading}
            accessCodeMapDefaultSetId={this.state.accessCodeMapDefaultSetId}
            accuracyTemplateCollections={this.state.accuracyTemplateCollections}
            accuracySpecialtyDefinitions={this.state.accuracySpecialtyDefinitions}
            authorizedInsurances={this.state.authorizedInsurances}
            authorizedInsurancesErred={this.state.authorizedInsurancesErred}
            baseUrl={this.state.baseUrl}
            completionTracker={completionTracker}
            createProjectModel={this.state.createProjectModel}
            handleAccessCodeMapSetChange={this.handleAccessCodeMapSetChange}
            handleAccuracyTemplateCollectionsChange={this.handleAccuracyTemplateCollectionsChange}
            handleAccuracyTemplateCorrelationIdsChange={this.handleAccuracyTemplateCorrelationIdsChange}
            handleAccuracySpecialtyDefChange={this.handleAccuracySpecialtyDefChange}
            handleAddInspectedInsurance={this.handleAddInspectedInsurance}
            handleAuthorizedInsurancesChange={this.handleAuthorizedInsurancesChange}
            handleMarketSpecialtyCodeMapSetChange={this.handleMarketSpecialtyCodeMapSetChange}
            handleNetworkSpecialtyCodeMapSetChange={this.handleNetworkSpecialtyCodeMapSetChange}
            handleRemoveInspectedInsurance={this.handleRemoveInspectedInsurance}
            handleSpecialtyGroupsChange={this.handleSpecialtyGroupsChange}
            handleSpecialtyToQdpCodeMapSetChange={this.handleSpecialtyToQdpCodeMapSetChange}
            handleTelehealthSpecialtyGroupsChange={this.handleTelehealthSpecialtyGroupChange}
            handleTemplateCollectionsChange={this.handleTemplateCollectionsChange}
            handleTemplateCorrelationIdsChange={this.handleTemplateCorrelationIdsChange}
            handleTimeDistanceCalculationTypeChange={this.handleTimeDistanceCalculationTypeChange}
            handleUseAccuracyChange={this.handleUseAccuracyChange}
            handleUseAdequacyChange={this.handleUseAdequacyChange}
            isZipProject={this.state.isZipProjectTemplate}
            originalTemplateCollections={this.state.originalTemplateCollections}
            progressList={this.state.progressList}
            projectAccessCodeMapSets={this.state.projectAccessCodeMapSets}
            projectCollections={this.state.projectCollections}
            projectTemplateCollections={this.state.projectTemplateCollections}
            selectedInsuranceUid={this.state.selectedInsuranceUid}
            selectedInsurances={this.state.selectedInsurances}
            showNetworkSpecialtyCodeMap={this.state.showNetworkSpecialtyCodeMap}
            specialtyCodeMapSets={this.state.specialtyCodeMapSets}
            specialtyDefinitionsMissing={this.state.specialtyDefinitionsMissing}
            specialtyGroups={this.state.specialtyGroups}
            specialtyToQdpCodeMapSets={this.state.specialtyToQdpCodeMapSets}
            timeDistanceCalculatorTypes={this.state.timeDistanceCalculatorTypes}
            useAccuracy={this.state.useAccuracy}
            useAdequacy={this.state.useAdequacy}
            user={user}
            currentIndex={index}
          />
        )}
      </UserContext.Consumer>);
    //we need to have values for either adequacy or accuracy
    completionTracker.push(accuracyTemplateComplete || adequacyTemplateComplete)

    steps.push((props, index) =>
      <CreateProjectStepMembers
        {...props}
        alternateMemberships={this.state.alternateMemberships}
        baseUrl={this.state.baseUrl}
        completionTracker={completionTracker}
        createProjectModel={this.state.createProjectModel}
        deleteAltMembershipRow={this.deleteAltMembershipRow}
        disableMemberBindingTable={this.state.disableMemberBindingTable}
        onNextClick={this.updateExclusions}
        generateAlternateMemberships={this.generateAltMemberships}
        handleMemberPopAliasChange={this.handleMemberPopAliasChange}
        handleMemberPopAliasChange2={this.handleMemberPopAliasChange2}
        handleMemberPopChange={this.handleMemberPopChange}
        handleMemberPopChange2={this.handleMemberPopChange2}
        handleRiskPopChange={this.handleRiskPopChange}
        handleSpecialtyGroupsChange={this.handleSpecialtyGroupsChange}
        isZipProject={this.state.isZipProjectTemplate}
        projectMemberPopulations={this.state.projectMemberPopulations}
        progressList={this.state.progressList}
        handlePrimaryMembershipBindingChange={this.handlePrimaryMembershipBindingChange}
        handleSecondaryMembershipBindingChange={this.handleSecondaryMembershipBindingChange}
        specialtyGroups={this.state.specialtyGroups}
        specialtyOverrides={this.state.specialtyOverrides}
        setPrimarySpecialtyOverride={this.setPrimarySpecialtyOverride}
        setSecondarySpecialtyOverride={this.setSecondarySpecialtyOverride}
        handleAlternativeMemberPopChange={this.handleAlternativeMemberPopChange}
        handleAlternativeMemberPopAliasChange={this.handleAlternativeMemberPopAliasChange}
        handleNewAlternativeMemberPop={this.handleNewAlternativeMemberPop}
        user={this.props.user}
        currentIndex={index}
      />);
    completionTracker.push(accuracyTemplateComplete || (this.state.createProjectModel.memberSetId !== null && adequacyTemplateComplete));

    steps.push((props, index) =>
      <CreateProjectStepProviders
        {...props}
        enabledFeatureFlags={this.props.enabledFeatureFlags}
        completionTracker={completionTracker}
        createProjectModel={this.state.createProjectModel}
        baseUrl={this.state.baseUrl}
        hideUseInMarketAnalysis={this.state.hideShowInMarketAnalysis}
        isZipProject={this.state.isZipProjectTemplate}
        progressList={this.state.progressList}
        onProvidersChange={this.handleProvidersChange}
        onProvidersAliasChange={this.handleProvidersAliasChange}
        onProspectsChange={this.handleProspectsChange}
        onProspectAliasChange={this.handleProspectAliasChange}
        onUseMarketChange={this.handleUseMarketChange}
        onMarketChange={this.handleMarketChange}
        onMarketAliasChange={this.handleMarketAliasChange}
        onUseMarketAnalysisChange={this.handleUseMarketForAnalysisChange}
        onUseNetworkAnalysisChange={this.handleUseNetworkForAnalysisChange}
        onUseProspectAnalysisChange={this.handleUseProspectForAnalysisChange}
        currentIndex={index}
        networkAlias={this.state.createProjectModel.networkProviderSetAlias}
        prospectAlias={this.state.createProjectModel.prospectProviderSetAlias}
        marketAlias={this.state.createProjectModel.marketProviderSetAlias}
      />);
    completionTracker.push(this.state.createProjectModel.networkProviderSetId !== null &&
      this.state.createProjectModel.networkProviderSetId !== 0);

    steps.push((props, index) =>
      <CreateProjectStepNetworkTaxonomy
        {...props}
        baseUrl={this.state.baseUrl}
        completionTracker={completionTracker}
        networkTaxonomyData={this.state.createProjectModel.networkTaxonomy}
        onNetworkTaxonomyChange={this.handleNetworkTaxonomyChange}
        progressList={this.state.progressList}
        currentIndex={index}
        revertTaxonomyData={this.state.networkProviderSetNetworkTaxonomy}
      />);
    completionTracker.push(this.state.isNetworkTaxonomyValid);

    steps.push((props, index) => <CreateProjectStepServiceArea
      {...props}
      completionTracker={completionTracker}
      createProjectModel={this.state.createProjectModel}
      baseUrl={this.state.baseUrl}
      handleCountyStatusUpdates={this.handleCountyStatusUpdates}
      handleCountyStatusForCheckedState={this.handleCountyStatusForCheckedState}
      isZipProject={this.state.isZipProjectTemplate}
      progressList={this.state.progressList}
      lookupRegionSet={this.state.lookupRegionSet}
      updateCounties={this.state.updateCounties}
      updateLookupRegionSet={this.updateLookupRegionSet}
      onRegionSetChange={this.handleRegionSetChange}
      onSubmitNearbyCounties={this.handleSubmitNearbyCounties}
      registerNearbyCountiesTriggerClose={this.registerNearbyCountiesTriggerClose}
      serviceAreaRegionSet={this.state.serviceAreaRegionSet}
      currentIndex={index}
    />);
    completionTracker.push(JSON.stringify(this.state.lookupRegionSet) !== "{}" && this.state.lookupRegionSet !== null);

    steps.push((props, index) =>
      <CreateProjectStepOptions
        {...props}
        baseUrl={this.state.baseUrl}
        completionTracker={completionTracker}
        createProjectModel={this.state.createProjectModel}
        handleUpdate={this.handleOptionUpdate}
        onNextClick={this.updateExclusions}
        progressList={this.state.progressList}
        hideCareJourneyScores={this.state.hideCareJourneyScores}
        hideClosestProviderReportOption={this.state.hideClosestProviderReportOption}
        hideCMSApplicationCredit={this.state.hideCMSApplicationCreditOption}
        hideCreateStandards={this.state.hideStandardsCreation}
        hidePracAtLocCondifence={!this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_PRAC_AT_LOC_CONFIDENCE)}
        hideProviderClaimsActivity={!this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_PROVIDER_CLAIMS_ACTIVITY)}
        hideMotiveAppropriatenessScore={this.state.hideMotiveAppropriatenessScore}
        isZipProject={this.state.isZipProjectTemplate}
        showNetworkIntegrityDashboard={this.props.enabledFeatureFlags.has(Features.ENABLE_NETWORK_INTEGRITY_DASHBOARD)}
        currentIndex={index}
        hideEnterpriseComplianceDashboard={this.state.hideEnterpriseComplianceDashboard}
        hideStrictTimeDistance={this.state.hideStrictTimeDistance}
      />);
    completionTracker.push(true);

    steps.push((props, index) => <CreateProjectStepReview
      {...props}
      completionTracker={completionTracker}
      createProjectModel={this.state.createProjectModel}
      baseUrl={this.state.baseUrl}
      progressList={this.state.progressList}
      removeNonAlternateMemberships={this.removeNonAlternateMemberships}
      currentIndex={index}
      hideCareJourneyScores={this.state.hideCareJourneyScores}
      hideMotiveAppropriatenessScore={this.state.hideMotiveAppropriatenessScore}
      hideStrictTimeDistance={this.state.hideStrictTimeDistance}
      isZipProject={this.state.isZipProjectTemplate}
      hideEnterpriseComplianceDashboard={this.state.hideEnterpriseComplianceDashboard}
      hideCMSApplicationCredit={this.state.hideCMSApplicationCreditOption}
      hideClosestProviderReportOption={this.state.hideClosestProviderReportOption}
      hidePracAtLocCondifence={!this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_PRAC_AT_LOC_CONFIDENCE)}
      hideProviderClaimsActivity={!this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_PROVIDER_CLAIMS_ACTIVITY)}
      showNetworkIntegrityDashboard={this.props.enabledFeatureFlags.has(Features.ENABLE_NETWORK_INTEGRITY_DASHBOARD)}
    />)

    return (
      <Switch>
        {steps.map((step, index) => (
          <Route
            key={index}
            exact={true}
            path={generatePath(`${this.props.match.url}/:id`, { id: index + 1 })}
            render={(props) => step(props, index + 1)}
          />
        ))}
      </Switch>
    );
  }

  /*
   * Step 1: Name and Description
   */
  private handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      createProjectModel: {
        ...this.state.createProjectModel,
        name: e.target.value,
      },
    });
  }

  private handleDescChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      createProjectModel: {
        ...this.state.createProjectModel,
        description: e.target.value,
      },
    });
  }

  private handleTaggingChange = (tags: ResourceTagModel[]) => {
    this.setState({
      createProjectModel: {
        ...this.state.createProjectModel,
        tags
      }
    });
  }

  private handleIsPrivateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      createProjectModel: {
        ...this.state.createProjectModel,
        isPrivate: e.target.checked,
      },
    });
  }

  /*
   *  Step 2: Select Template
   */
  private handleRenderTemplateCollectionsList = async () => {
    const projectCollections = await this.templateService.getUnarchivedTemplateCollections();
    const accuracyTemplateCollections = await this.accuracyTemplateService.getTemplateCollections();
    const accuracySpecialtyDefinitions = await this.accuracyTemplateService.getSpecialtyDefinitions();

    this.setState({
      accuracySpecialtyDefinitions,
      accuracyTemplateCollections,
      projectCollections,
    });
  }

  private handleRenderTemplatesList = async () => {
    // const projectTemplates = (this.state.createProjectModel.categoryId !== null) ? await this.adequacyTemplateService.getTemplateCollectionById(this.state.createProjectModel.categoryId) : 0;
    // if (projectTemplates) {
    //   this.setState({ originalTemplates: projectTemplates, projectTemplates });
    // }
  }

  private handleRenderMemberPopList = async () => {
    const allMemberPopulations = await this.memberSetService.getActive(true, false);

    this.setState({
      allMemberPopulations,
      alternateMemberships: allMemberPopulations,
    });
  }

  /**
   * Loads up the available access region class map sets
   */
  private handleGatherCodeMapSets = async () => {
    const countyClassCodeMapSetsPromise = this.codeMapSetService.get(CodeMapType.CountyClassMap);
    const zipCodeCodeMapSetsPromise = this.codeMapSetService.get(CodeMapType.ZipCodeClassMap);
    const specialtyCodeMapSetsPromise = this.codeMapSetService.get(CodeMapType.SpecialtyCodeMap);
    const specialtyToQdpCodeMapSetsPromise = this.codeMapSetService.get(CodeMapType.SpecialtyToQDPCodeMap);

    this.setState({
      allAccessCodeMapSets: [...await countyClassCodeMapSetsPromise, ...await zipCodeCodeMapSetsPromise],
      specialtyCodeMapSets: await specialtyCodeMapSetsPromise,
      specialtyToQdpCodeMapSets: await specialtyToQdpCodeMapSetsPromise,
    });
  }

  private handleTemplateCollectionsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const templateCollection = +(e.target.value);
    const templateCollectionDesc = (e.target.options[e.target.selectedIndex].innerText);
    const template = this.state.projectCollections.find((t) => t.templateCollectionId === templateCollection);

    const isZipProject = template.resource.resourceType === ResourceType.AdequacyZipTemplateCollection;
    const thingsToResetIfZipProject: Partial<CreateProjectWizardState> = createThingsToResetIfZipProject(isZipProject);
    const projectThingsToResetIfZipProject: Partial<CreateProject> = createProjectThingsToResetIfZipProject(isZipProject);

    this.setState((prevState): CreateProjectWizardState => ({
      ...prevState,
      createProjectModel: {
        ...this.state.createProjectModel,
        accessCodeMapSetDescription: "",
        accessCodeMapSetId: null,
        adequacyTemplateCollectionDescription: templateCollectionDesc,
        adequacyTemplateCollectionId: templateCollection,
        adequacyTemplateCorrelationId: "",
        memberPopDescription: "",
        memberPopDescription2: "",
        memberSetAlias: "",
        memberSetAlias2: "",
        memberSetId: null,
        memberSetId2: null,
        timeDistanceCalculatorType: null,
        ...projectThingsToResetIfZipProject,
      },
      isZipProjectTemplate: isZipProject,
      projectAccessCodeMapSets: [],
      projectMemberPopulations: [],
      specialtyGroups: [],
      timeDistanceCalculatorTypes: [],
      ...thingsToResetIfZipProject,
    }));
  }

  private handleTemplateCorrelationIdsChange = async (e: React.ChangeEvent<HTMLSelectElement>, userRoles: string[]) => {
    const adequacyTemplateCorrelationId = e.target.value;
    const selectedCorrelationIdOrdinal = e.target.options[e.target.selectedIndex].index;

    if (selectedCorrelationIdOrdinal === 0) {
      this.setState((prevState): CreateProjectWizardState => ({
        ...prevState,
        createProjectModel: {
          ...prevState.createProjectModel,
          adequacyTemplateCorrelationId,
          excludedSpecialtyGroupCodes: [],
          telehealthSpecialtyGroupCodes: [],
          timeDistanceCalculatorType: null,
        },
        projectAccessCodeMapSets: [],
        projectMemberPopulations: [],
        specialtyGroups: [],
        timeDistanceCalculatorTypes: [],
      }));
    } else {
      // Setup the retrieval of the specialty groups while the member sets and code map sets are fetched.
      //TODO: add accuracy specialty groups retrieval
      const specialtyGroupsPromise = this.templateService.getLatestTemplateSpecialtyGroupsByCorrelationId(this.state.createProjectModel.adequacyTemplateCollectionId, adequacyTemplateCorrelationId);
      const templateMetadataPromise = this.templateService.getLatestTemplateByCorrelationId(this.state.createProjectModel.adequacyTemplateCollectionId, adequacyTemplateCorrelationId, false);

      let projectAccessCodeMapSets: CodeMapSet[] = this.state.allAccessCodeMapSets;
      const projectMemberPopulations: MemberSetModel[] = this.state.allMemberPopulations;
      let accessCodeMapDefaultSetId: number = this.state.accessCodeMapDefaultSetId;
      let accessCodeMapSetId: number = this.state.createProjectModel.accessCodeMapSetId;
      let memberSetId: number = this.state.createProjectModel.memberSetId;

      // Get to the template, using the chosen collection, and the correlation ID
      for (let collection of this.state.projectCollections) {
        if (collection.templateCollectionId === this.state.createProjectModel.adequacyTemplateCollectionId) {
          // Populate the collection with template information
          collection = await this.templateService.getTemplateCollectionById(collection.templateCollectionId);

          // Found the template collection
          for (const template of collection.templates) {
            if (template.correlationId === adequacyTemplateCorrelationId) {
              // Found the template - now filter down the access codeMaps available for selection to those that are compatible with the chosen template
              if (template.accessCriteriaSpecificationGranularity === AccessCriteriaGranularityEnum.ZipClass) {
                projectAccessCodeMapSets = projectAccessCodeMapSets.filter((r) => r.codeMapType === CodeMapType.ZipCodeClassMap);
              } else if (template.accessCriteriaSpecificationGranularity === AccessCriteriaGranularityEnum.CountyClass) {
                projectAccessCodeMapSets = projectAccessCodeMapSets.filter((r) => r.codeMapType === CodeMapType.CountyClassMap);
              } else {
                projectAccessCodeMapSets = [];
              }

              // Suggest the codemapset and memberset that are specified by the template, by moving them to the top of their respective list
              if (projectAccessCodeMapSets && projectAccessCodeMapSets.length > 0 && template.accessCodeMapSetId !== null && template.accessCodeMapSetId !== undefined) {
                projectAccessCodeMapSets.sort((x, y) => x.codeMapSetId === template.accessCodeMapSetId ? -1 : y.codeMapSetId === template.accessCodeMapSetId ? 1 : 0);
              }
              if (template.accessCodeMapSetId !== undefined && template.accessCodeMapSetId !== null && projectAccessCodeMapSets !== null && projectAccessCodeMapSets.length > 0 && projectAccessCodeMapSets[0].codeMapSetId === template.accessCodeMapSetId) {
                accessCodeMapDefaultSetId = projectAccessCodeMapSets[0].codeMapSetId;
                accessCodeMapSetId = projectAccessCodeMapSets[0].codeMapSetId;
              }
              if (projectMemberPopulations && projectMemberPopulations.length > 0 && template.memberSetId !== null && template.memberSetId !== undefined) {
                projectMemberPopulations.sort((x, y) => x.memberSetId === template.memberSetId ? -1 : y.memberSetId === template.memberSetId ? 1 : 0);
              }
              if (template.memberSetId !== undefined && template.memberSetId !== null && projectMemberPopulations !== null && projectMemberPopulations.length > 0 && projectMemberPopulations[0].memberSetId === template.memberSetId) {
                memberSetId = projectMemberPopulations[0].memberSetId;
              }
            }
          }
        }
      }

      const specialtyGroups = await specialtyGroupsPromise;
      const templateMetadata = await templateMetadataPromise;
      this.generateSpecialtyOverrides(specialtyGroups);

      const specialtyDefPromise =
        this.specialtyDefinitionService.getSpecialtyDefinitionById(templateMetadata.specialtyDefinitionId);

      let specialtyDefinition: SpecialtyDefinition = null;

      try {
        specialtyDefinition = await specialtyDefPromise;
      } catch (e) {
        specialtyDefinition = null;
      }

      const roleCalculatorTypeMap: { [key: string]: TimeDistanceCalculatorType } = {
        [Roles.GEOGRAPHY_AWARE_DISTANCE_USER]: TimeDistanceCalculatorType.GeographyAware,
        [Roles.ROUTING_DISTANCE_USER]: TimeDistanceCalculatorType.GraphhopperRouting,
        [Roles.STRAIGHT_LINE_DISTANCE_USER]: TimeDistanceCalculatorType.StraightLine,
      };

      const filteredTypes: TimeDistanceCalculatorType[] = templateMetadata.timeDistanceCalculatorTypes?.filter(t =>
        t === TimeDistanceCalculatorType.Quest || userRoles.find(r => roleCalculatorTypeMap[r] === t)) ?? [];

      const hasCmsApplicationCredits: boolean = specialtyGroups.some(specialtyGroup => specialtyGroup.hasCMSApplicationCreditPercentages);

      const showMotiveAppropriatenessScore = templateMetadata.motiveCrosswalkCodeMapSetId !== undefined && templateMetadata.motiveCrosswalkCodeMapSetId !== null;

      this.setState((prevState): CreateProjectWizardState => ({
        ...prevState,
        accessCodeMapDefaultSetId,
        createProjectModel: {
          ...this.state.createProjectModel,
          accessCodeMapSetId,
          adequacyTemplateCorrelationId,
          excludedSpecialtyGroupCodes: [],
          memberSetId,
          showCMSCredit: !hasCmsApplicationCredits ? false : this.state.createProjectModel.showCMSCredit,
          showMotiveAppropriatenessScore: !showMotiveAppropriatenessScore ? false : this.state.createProjectModel.showMotiveAppropriatenessScore,
          telehealthSpecialtyGroupCodes: [],
          timeDistanceCalculatorType: (filteredTypes && filteredTypes.length > 0)
            ? prevState.createProjectModel.timeDistanceCalculatorType
              ? filteredTypes.find(t => t === prevState.createProjectModel.timeDistanceCalculatorType)
                ? filteredTypes.find(t => t === prevState.createProjectModel.timeDistanceCalculatorType)
                : filteredTypes[0]
              : filteredTypes[0]
            : null,
        },
        hideCMSApplicationCreditOption: !hasCmsApplicationCredits,
        hideMotiveAppropriatenessScore: !showMotiveAppropriatenessScore,
        projectAccessCodeMapSets,
        projectMemberPopulations,
        specialtyDefinitionsMissing: !specialtyDefinition,
        specialtyGroups,
        timeDistanceCalculatorTypes: templateMetadata.timeDistanceCalculatorTypes,
      }));
    }
  }

  private handleTimeDistanceCalculationTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const timeDistanceCalculatorType = e.target.value as TimeDistanceCalculatorType;

    this.setState({
      createProjectModel: {
        ...this.state.createProjectModel,
        timeDistanceCalculatorType,
      },
    });
  }

  private handleAccuracyTemplateCollectionsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const accuracyTemplateCollectionId = e.target.value;
    const accuracyTemplateCollectionDescription = e.target.options[e.target.selectedIndex].innerText;

    this.setState((prevState) => ({
      createProjectModel: {
        ...prevState.createProjectModel,
        accuracyTemplateCollectionDescription,
        accuracyTemplateCollectionId,
        accuracyTemplateCorrelationId: "",
      },
    }));
  }

  private handleAccuracyTemplateCorrelationIdsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const accuracyTemplateCorrelationId = e.target.value;

    this.setState((prevState) => ({
      ...prevState,
      createProjectModel: {
        ...prevState.createProjectModel,
        accuracyTemplateCorrelationId,
      },
    }));
  }

  private handleAccuracySpecialtyDefChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const accuracySpecialtyDefinitionId = e.target.value;
    const accuracySpecialtyDefinitionDesc = e.target.options[e.target.selectedIndex].innerText;

    //reset specialty group codes accordion, then reload w/ new values
    this.setState((prevState): CreateProjectWizardState => ({
      ...prevState,
      createProjectModel: {
        ...prevState.createProjectModel,
        accuracySpecialtyDefinitionDesc,
        accuracySpecialtyDefinitionId,
        excludedSpecialtyGroupCodes: [],
        telehealthSpecialtyGroupCodes: [],
      },
      specialtyGroups: [],
    }));

    const specialtyDefinition = await this.accuracyTemplateService
      .getSpecialtyDefinitionById(accuracySpecialtyDefinitionId);

    const specialtyGroups: TemplateSpecialtyGroup[] = [];

    for (const category of specialtyDefinition.categories) {
      for (const specialtyGroup of category.specialtyGroups) {
        const templateSpecialtyGroup: TemplateSpecialtyGroup = {
          code: specialtyGroup.code,
          displayOrder: specialtyGroup.displayOrder,
          hasTelehealthAccessPercentageCredits: null,
          name: specialtyGroup.name,
          servicingCountBy: null,
          servicingMinimumType: null,
        };

        specialtyGroups.push(templateSpecialtyGroup);
      }
    }

    this.setState((prevState) => ({
      ...prevState,
      createProjectModel: {
        ...prevState.createProjectModel,
      },
      specialtyGroups,
    }));
  }

  private handleAuthorizedInsurancesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    //set state for currently selected insurance
    const uid = e.target.value;

    this.setState((prevState) => ({
      ...prevState,
      selectedInsuranceUid: uid,
    }));
  }

  private handleAddInspectedInsurance = () => {
    const uid = this.state.selectedInsuranceUid;
    const selectedInsurances = this.state.selectedInsurances;

    if (!uid) { return; }

    //get the insurance we want to add
    const insToAdd = this.state.authorizedInsurances.find((ins) => {
      return ins.uid === uid;
    });

    //check if insurance we want to add is already in list of selected insurances
    const existingIns = selectedInsurances.find((ins) => {
      return ins.uid === uid;
    });

    if (insToAdd && !existingIns) {
      selectedInsurances.push(insToAdd);

      const sortedSelectedInsurances = selectedInsurances && sortBy(selectedInsurances, "planName");

      this.setState((prevState) => ({
        ...prevState,
        createProjectModel: {
          ...prevState.createProjectModel,
          inspectedInsurancesList: sortedSelectedInsurances,
        },
        selectedInsurances: sortedSelectedInsurances,
      }));
    }
  }

  private handleRemoveInspectedInsurance = (uid: string) => {
    const selectedInsurances = this.state.selectedInsurances;
    const index = selectedInsurances.findIndex((ins) => ins.uid === uid);

    if (uid && index > -1) {
      selectedInsurances.splice(index, 1);

      this.setState((prevState) => ({
        ...prevState,
        createProjectModel: {
          ...prevState.createProjectModel,
          inspectedInsurancesList: selectedInsurances,
        },
        selectedInsurances,
      }));
    }
  }

  private handleUseAccuracyChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = !!e.target.checked;
    const accuracyAndAdequacy = checked && this.state.useAdequacy;

    let sortedAuthorizedInsurances: ClientInsuranceModel[] = [...this.state.authorizedInsurances];
    let erred = false;

    if (checked && sortedAuthorizedInsurances.length === 0) {
      try {
        this.setState((prevState) => ({ ...prevState, accuracyIsLoading: true }));
        const authorizedInsurances = await this.insuranceService.getAuthorizedInsurancesByClientId();
        sortedAuthorizedInsurances = authorizedInsurances && sortBy(authorizedInsurances, "planName");
      }
      catch {
        erred = true;
      }
    }

    this.setState((prevState): CreateProjectWizardState => ({
      ...prevState,
      accuracyIsLoading: false,
      authorizedInsurances: sortedAuthorizedInsurances,
      authorizedInsurancesErred: erred,
      createProjectModel:
      {
        ...prevState.createProjectModel,
        accuracyAndAdequacy: accuracyAndAdequacy,
        accuracySpecialtyDefinitionDesc: null,
        accuracySpecialtyDefinitionId: null,
        accuracyTemplateCollectionDescription: null,
        accuracyTemplateCollectionId: null,
        accuracyTemplateCorrelationId: null,
        inspectedInsurancesList: [],
      },
      selectedInsurances: [],
      useAccuracy: checked,
    }));
  }

  private handleUseAdequacyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = !!e.target.checked;
    const accuracyAndAdequacy = checked && this.state.useAccuracy;

    //reset adequacy fields when check box changes
    this.setState((prevState): CreateProjectWizardState => ({
      ...prevState,
      createProjectModel:
      {
        ...prevState.createProjectModel,
        accessCodeMapSetDescription: "",
        accessCodeMapSetId: null,
        accuracyAndAdequacy: accuracyAndAdequacy,
        accuracySpecialtyDefinitionDesc: null,
        accuracySpecialtyDefinitionId: null,
        adequacyTemplateCollectionDescription: "",
        adequacyTemplateCollectionId: null,
        adequacyTemplateCorrelationId: null,
        displayOnNetworkAccordion: !checked ? null : prevState.createProjectModel.displayOnNetworkAccordion,
        excludedSpecialtyGroupCodes: [],
        inspectedInsurancesList: [],
        marketProviderSetId: null,
        marketSpecialtyCodeMapSetId: null,
        memberPopDescription: "",
        memberPopDescription2: "",
        memberSetAlias: null,
        memberSetAlias2: null,
        memberSetId: null,
        memberSetId2: null,
        networkProviderSetId: null,
        networkSpecialtyCodeMapSetId: null,
        prospectProviderSetId: null,
        showAffiliationsInsights: !checked ? null : prevState.createProjectModel.showAffiliationsInsights,
        showCareJourneyScores: !checked ? null : prevState.createProjectModel.showCareJourneyScores,
        showLocationConfidenceScores: !checked ? null : prevState.createProjectModel.showLocationConfidenceScores,
        showMotiveAppropriatenessScore: !checked ? null : prevState.createProjectModel.showMotiveAppropriatenessScore,
        showPatternOfCareProviders: !checked ? null : prevState.createProjectModel.showPatternOfCareProviders,
        showPciDashboard: !checked ? null : prevState.createProjectModel.showPciDashboard,
        showPciData: !checked ? null : prevState.createProjectModel.showPciData,
        showTricareClaimsVolumeColumn: !checked ? null : prevState.createProjectModel.showTricareClaimsVolumeColumn,
        showVaClaimsVolumeColumn: !checked ? null : prevState.createProjectModel.showVaClaimsVolumeColumn,
        telehealthSpecialtyGroupCodes: [],
        useMarketProvidersForImpact: null,
      },
      projectAccessCodeMapSets: [],
      projectMemberPopulations: [],
      //selectedSpecialtyGroupCodes: [],
      specialtyGroups: [],
      useAdequacy: checked,
    }));
  }

  private handleNetworkSpecialtyCodeMapSetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const networkSpecialtyCodeMapSetId = +(e.target.value);

    this.setState({
      createProjectModel: {
        ...this.state.createProjectModel,
        networkSpecialtyCodeMapSetId,
      },
    });
  }

  private handleMarketSpecialtyCodeMapSetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const marketSpecialtyCodeMapSetId = +(e.target.value);

    this.setState({
      createProjectModel: {
        ...this.state.createProjectModel,
        marketSpecialtyCodeMapSetId,
      },
    });
  }

  private handleSpecialtyGroupsChange = (selectedRecords: TemplateSpecialtyGroup[]) => {
    const specialtyGroupCodes = this.state.specialtyGroups.map((sg) => sg.code);
    const selectedSpecialtyGroupCodes = selectedRecords.map((sg) => sg.code);
    const excludedSpecialtyGroupCodes = difference(specialtyGroupCodes, selectedSpecialtyGroupCodes);

    this.setState((prevState) => ({
      ...prevState,
      createProjectModel: {
        ...prevState.createProjectModel,
        excludedSpecialtyGroupCodes,
      },
    }));
  }

  private handleTelehealthSpecialtyGroupChange = (selectedSpecialtyGroupCodes: string[]) => {
    this.setState((prevState) => {
      return (
        {
          ...prevState,
          createProjectModel: {
            ...prevState.createProjectModel,
            telehealthSpecialtyGroupCodes: selectedSpecialtyGroupCodes,
          },
        });
    },
    );
  }

  private handleMemberPopAliasChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { createProjectModel } = this.state;
    const updates: CreateProjectOverrideMemberSet[] = createProjectModel.createProjectOverrideMemberSets;
    const memberSetAlias = e.target.value;
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Primary) {
        x.alias = e.target.value;
      }
    });

    this.setState((prevState) => ({
      ...prevState,
      createProjectModel: {
        ...prevState.createProjectModel,
        createProjectOverrideMemberSets: updates,
        memberSetAlias,
      },
    }));
  }

  private handleMemberPopAliasChange2 = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { createProjectModel } = this.state;
    const updates: CreateProjectOverrideMemberSet[] = createProjectModel.createProjectOverrideMemberSets;
    const memberSetAlias2 = e.target.value;
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Secondary) {
        x.alias = e.target.value;
      }
    });

    this.setState((prevState) => ({
      ...prevState,
      createProjectModel: {
        ...prevState.createProjectModel,
        createProjectOverrideMemberSets: updates,
        memberSetAlias2,
      },
    }));
  }

  private toggleShowClosestProviderReportOption = () => {
    const { createProjectModel } = this.state;
    const currentMemberPops = this.state.allMemberPopulations.filter(function (pop) {
      return (pop.memberSetId == createProjectModel.memberSetId || pop.memberSetId == createProjectModel.memberSetId2 ||
        createProjectModel.createProjectOverrideMemberSets.some(function (overrideMemberSet) { return pop.memberSetId == overrideMemberSet.memberSetId }))
    })

    const anyCensus = currentMemberPops.some(function (pop) {
      return pop.memberSetType == MemberSetTypeEnum.Census;
    })

    let hideClosestProviderReportOption = false;
    if (anyCensus) {
      hideClosestProviderReportOption = true;
    }

    this.setState((prevState) => ({
      ...prevState,
      createProjectModel: {
        ...prevState.createProjectModel,
        showClosestProviderReport: false,
      },
      hideClosestProviderReportOption: hideClosestProviderReportOption,
    }));
  }

  private handleMemberPopChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const memberPop = +(e.target.value);
    const memberPopDesc = (e.target.options[e.target.selectedIndex].innerText);

    const { createProjectModel } = this.state;
    const updates: CreateProjectOverrideMemberSet[] = createProjectModel.createProjectOverrideMemberSets;
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Primary) {
        x.memberSetId = memberPop;
      }
    });

    this.setState((prevState) => ({
      ...prevState,
      createProjectModel: {
        ...prevState.createProjectModel,
        createProjectOverrideMemberSets: updates,
        memberPopDescription: memberPop ? memberPopDesc : "",
        memberSetId: memberPop ? memberPop : null,
      },
    }), this.toggleShowClosestProviderReportOption);
  }

  private handleMemberPopChange2 = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const memberPop = +(e.target.value);
    const memberPopDesc = (e.target.options[e.target.selectedIndex].innerText);

    const { createProjectModel } = this.state;
    const updates: CreateProjectOverrideMemberSet[] = createProjectModel.createProjectOverrideMemberSets;
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Secondary) {
        x.memberSetId = memberPop;
      }
    });

    this.setState((prevState) => ({
      ...prevState,
      createProjectModel: {
        ...prevState.createProjectModel,
        createProjectOverrideMemberSets: updates,
        memberPopDescription2: memberPop ? memberPopDesc : "",
        memberSetId2: memberPop ? memberPop : null,
      },
    }), this.toggleShowClosestProviderReportOption);
  }

  private generateAltMemberships = () => {
    const { createProjectModel } = this.state;
    const updates: CreateProjectOverrideMemberSet[] = [...createProjectModel.createProjectOverrideMemberSets];
    const { projectMemberPopulations } = this.state;
    let altMembers: MemberSetModel[] = [];
    let empty = true;
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate) {
        empty = false;
        projectMemberPopulations.forEach((y) => {
          if (x.memberSetId === y.memberSetId) {
            altMembers.push(y);
          }
        });
      }
    });
    if (empty === true) {
      altMembers = this.state.projectMemberPopulations;
    }
    this.setState((prevState) => ({
      ...prevState,
      alternateMemberships: altMembers,
    }));
  }

  private handleNewAlternativeMemberPop = () => {
    const newAltMember: CreateProjectOverrideMemberSet = {
      alias: "",
      customProjectId: null,
      memberSetId: null,
      memberSetName: "",
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetType: ProjectOverrideMemberSetType.Alternate,
      secondarySpecialtyGroupCodes: [],
    };

    const { createProjectModel } = this.state;
    const updates: CreateProjectOverrideMemberSet[] = [...createProjectModel.createProjectOverrideMemberSets];
    updates.push(newAltMember);
    this.toggleMemberBinding();

    this.setState((prevState) => ({
      ...prevState,
      createProjectModel: {
        ...prevState.createProjectModel,
        createProjectOverrideMemberSets: updates,
      },
    }));
  }

  private toggleMemberBinding = () => {
    let tracker = false;
    const { createProjectModel } = this.state;
    const overrides: CreateProjectOverrideMemberSet[] = createProjectModel.createProjectOverrideMemberSets;
    overrides.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate && x.memberSetId != null) {
        this.setState((prevState) => ({ ...prevState, disableMemberBindingTable: false }));
        tracker = true;
      }
    });
    if (tracker === false) {
      this.setState((prevState) => ({ ...prevState, disableMemberBindingTable: true }));
    }
  }

  private handleAlternativeMemberPopChange = (memberPopId: number, index: number) => {
    const { createProjectModel } = this.state;
    const updates: CreateProjectOverrideMemberSet[] = [...createProjectModel.createProjectOverrideMemberSets];
    const memberName = this.state.allMemberPopulations.filter((ind) => ind.memberSetId === memberPopId)[0]?.resource.name;
    const x = updates[index];
    x.memberSetId = memberPopId;
    x.memberSetName = memberName;
    x.primarySpecialtyGroupCodes.forEach((y) => {
      this.setPrimarySpecialtyOverride(memberPopId, y);
    });
    x.secondarySpecialtyGroupCodes.forEach((y) => {
      this.setSecondarySpecialtyOverride(memberPopId, y);
    });

    this.setState((prevState) => ({
      ...prevState,
      createProjectModel: {
        ...prevState.createProjectModel,
        createProjectOverrideMemberSets: updates,
      },
    }), this.toggleShowClosestProviderReportOption);
    this.toggleMemberBinding();
  }

  private handleAlternativeMemberPopAliasChange = (alias: string, index: number) => {
    const { createProjectModel } = this.state;
    const updates: CreateProjectOverrideMemberSet[] = [...createProjectModel.createProjectOverrideMemberSets];
    updates[index].alias = alias;

    this.setState((prevState) => ({
      ...prevState,
      createProjectModel: {
        ...prevState.createProjectModel,
        createProjectOverrideMemberSets: updates,
      },
    }));
  }

  private populateOverride = () => {
    const primary: CreateProjectOverrideMemberSet = {
      alias: "",
      customProjectId: null,
      memberSetId: null,
      memberSetName: null,
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetType: ProjectOverrideMemberSetType.Primary,
      secondarySpecialtyGroupCodes: [],
    };
    const secondary: CreateProjectOverrideMemberSet = {
      alias: "",
      customProjectId: null,
      memberSetId: null,
      memberSetName: null,
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetType: ProjectOverrideMemberSetType.Secondary,
      secondarySpecialtyGroupCodes: [],
    };
    const risk: CreateProjectOverrideMemberSet = {
      alias: "",
      customProjectId: null,
      memberSetId: null,
      memberSetName: "Risk Analysis Membership",
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetType: ProjectOverrideMemberSetType.Risk,
      secondarySpecialtyGroupCodes: [],
    };
    const createProjectOverrideMemberSets: CreateProjectOverrideMemberSet[] = [];
    createProjectOverrideMemberSets.push(primary);
    createProjectOverrideMemberSets.push(secondary);
    if (some(this.props.user.roles, ((s) => s === Roles.MEMBERSHIP_RISK_USER))) { createProjectOverrideMemberSets.push(risk); }
    return createProjectOverrideMemberSets;
  }

  private deleteAltMembershipRow = (index: number) => {
    const { createProjectModel } = this.state;
    const updates: CreateProjectOverrideMemberSet[] = [...createProjectModel.createProjectOverrideMemberSets];
    //for (let i = index; i < updates.length - 1; i += 1) {
    //  updates[i] = { ...updates[i + 1] };
    //}
    updates.splice(index, 1);
    this.setState((prevState) => ({
      ...prevState,
      createProjectModel: {
        ...prevState.createProjectModel,
        createProjectOverrideMemberSets: updates,
      },
    }), () => {
      this.generateAltMemberships();
      this.toggleMemberBinding();
      this.toggleShowClosestProviderReportOption();
    });
  }

  private removeNonAlternateMemberships = () => {
    const { createProjectModel } = this.state;
    const updates: CreateProjectOverrideMemberSet[] = createProjectModel.createProjectOverrideMemberSets;
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Primary || x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Secondary || x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Risk) {
        updates.splice(updates.indexOf(x), 1);
      }
    });
    this.setState((prevState) => ({
      ...prevState,
      createProjectModel: {
        ...prevState.createProjectModel,
        createProjectOverrideMemberSets: updates,
      },
    }));
  }

  private handlePrimaryMembershipBindingChange = (memberPop: number, code: string) => {
    const { createProjectModel } = this.state;
    const updates: CreateProjectOverrideMemberSet[] = createProjectModel.createProjectOverrideMemberSets;
    if (memberPop === null) {
      updates.forEach((x) => {
        x.primarySpecialtyGroupCodes.forEach((y) => {
          if (y === code) {
            x.primarySpecialtyGroupCodes.splice(x.primarySpecialtyGroupCodes.indexOf(y), 1);
          }
        });
      });
    } else {
      updates.forEach((x) => {
        if (x.memberSetId === memberPop && !x.primarySpecialtyGroupCodes.find(p => p === code)) {
          x.primarySpecialtyGroupCodes.push(code);
        }
      });
    }
    this.setState((prevState) => ({
      ...prevState,
      createProjectModel: {
        ...prevState.createProjectModel,
        createProjectOverrideMemberSets: updates,
      },
    }));
  }

  private handleSecondaryMembershipBindingChange = (memberPop: number, code: string) => {
    const { createProjectModel } = this.state;
    const updates: CreateProjectOverrideMemberSet[] = createProjectModel.createProjectOverrideMemberSets;
    if (memberPop === null) {
      updates.forEach((x) => {
        x.secondarySpecialtyGroupCodes.forEach((y) => {
          if (y === code) {
            x.secondarySpecialtyGroupCodes.splice(x.secondarySpecialtyGroupCodes.indexOf(y), 1);
          }
        });
      });
    } else {
      updates.forEach((x) => {
        if (x.memberSetId === memberPop) {
          x.secondarySpecialtyGroupCodes.push(code);
        }
      });
    }
    this.setState((prevState) => ({
      ...prevState,
      createProjectModel: {
        ...prevState.createProjectModel,
        createProjectOverrideMemberSets: updates,
      },
    }));
  }

  private handleRiskPopChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const riskPop = +(e.target.value);
    const riskDescription = (e.target.options[e.target.selectedIndex].innerText);

    const { createProjectModel } = this.state;
    const updates: CreateProjectOverrideMemberSet[] = createProjectModel.createProjectOverrideMemberSets;
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Risk) {
        x.memberSetId = riskPop;
      }
    });

    if (riskPop) {
      this.setState((prevState) => ({
        ...prevState,
        createProjectModel: {
          ...prevState.createProjectModel,
          createProjectOverrideMemberSets: updates,
          membershipRiskMemberSetAlias: riskDescription,
          membershipRiskMemberSetId: riskPop,
        },
      }));
    } else {
      this.setState((prevState): CreateProjectWizardState => ({
        ...prevState,
        createProjectModel: {
          ...prevState.createProjectModel,
          membershipRiskMemberSetAlias: "",
          membershipRiskMemberSetId: null,
        },
      }));
    }

  }

  private generateSpecialtyOverrides = (sortedRecords: TemplateSpecialtyGroup[]) => {
    const specialtyOverrides: SpecialtyOverrides[] = [];
    sortedRecords.forEach((x) => {
      specialtyOverrides.push({ code: x.code, primaryMembership: null, secondaryMembership: null });
    });
    this.setState((prevState) => ({
      ...prevState,
      specialtyOverrides,
    }));
  }

  private setPrimarySpecialtyOverride = (memberPop: number, code: string) => {
    const { specialtyOverrides } = this.state;
    specialtyOverrides.forEach((x) => {
      if (x.code === code) {
        x.primaryMembership = memberPop;
      }
    });
    this.setState((prevState) => ({
      ...prevState,
      specialtyOverrides,
    }));
  }

  private setSecondarySpecialtyOverride = (memberPop: number, code: string) => {
    const { specialtyOverrides } = this.state;
    specialtyOverrides.forEach((x) => {
      if (x.code === code) {
        x.secondaryMembership = memberPop;
      }
    });
    this.setState((prevState) => ({
      ...prevState,
      specialtyOverrides,
    }));
  }

  private handleAccessCodeMapSetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const accessCodeMapSetId = +(e.target.value);
    const accessCodeMapSetDescription = (e.target.options[e.target.selectedIndex].innerText);

    this.setState((prevState) => ({
      ...prevState,
      createProjectModel: {
        ...prevState.createProjectModel,
        accessCodeMapSetDescription: accessCodeMapSetId ? accessCodeMapSetDescription : "",
        accessCodeMapSetId: accessCodeMapSetId ? accessCodeMapSetId : null,
      },
    }));
  }

  private handleSpecialtyToQdpCodeMapSetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const codeMapSetId = +(e.target.value);
    const codeMapSetDescription = (e.target.options[e.target.selectedIndex].innerText);

    this.setState({
      createProjectModel: {
        ...this.state.createProjectModel,
        accuracySpecialtyToQdpCodeMapSetDescription: codeMapSetDescription ? codeMapSetDescription : "",
        accuracySpecialtyToQdpCodeMapSetId: codeMapSetId ? codeMapSetId : null,
      },
    });
  }

  /*
   *  Step 4: Select Provider group or Employee group
   */
  private handleProvidersChange = (ps: ProviderSet) => {
    const networkProvider = (ps ? ps.resource.resourceId : null);
    const networkProviderDesc = (ps ? ps.resource.name : "");

    this.setState({
      createProjectModel: {
        ...this.state.createProjectModel,
        networkProviderDescription: networkProviderDesc,
        networkProviderSetId: networkProvider,
      },
    });
  }

  private handleProvidersAliasChange = (alias: string) => {
    this.setState((prevState) => ({
      createProjectModel: {
        ...prevState.createProjectModel,
        networkProviderSetAlias: alias,
      }
    }));
  }

  private handleProspectsChange = (ps: ProviderSet) => {
    const prospectProvider = (ps ? ps.resource.resourceId : null);
    const prospectProviderDesc = (ps ? ps.resource.name : "");

    this.setState({
      createProjectModel: {
        ...this.state.createProjectModel,
        prospectProviderDescription: prospectProviderDesc,
        prospectProviderSetId: prospectProvider,
      },
    });
  }

  private handleProspectAliasChange = (alias: string) => {
    this.setState((prevState) => ({
      createProjectModel: {
        ...prevState.createProjectModel,
        prospectProviderSetAlias: alias,
      }
    }));
  }

  private handleUseMarketChange = (state: boolean) => {
    this.setState({
      createProjectModel: {
        ...this.state.createProjectModel,
        useMarketProvidersForImpact: state,
        useMarketProvidersForMarketAnalysis: state,
      },
    });
  }

  private handleMarketAliasChange = (alias: string) => {
    this.setState((prevState) => ({
      createProjectModel: {
        ...prevState.createProjectModel,
        marketProviderSetAlias: alias,
      }
    }));
  }

  private handleUseMarketForAnalysisChange = (state: boolean) => {
    this.setState({
      createProjectModel: {
        ...this.state.createProjectModel,
        useMarketProvidersForImpact: state,
        useMarketProvidersForMarketAnalysis: state,
      },
    });
  }

  private handleUseProspectForAnalysisChange = (state: boolean) => {
    this.setState({
      createProjectModel: {
        ...this.state.createProjectModel,
        useProspectProvidersForMarketAnalysis: state,
      },
    });
  }

  private handleUseNetworkForAnalysisChange = (state: boolean) => {
    this.setState({
      createProjectModel: {
        ...this.state.createProjectModel,
        useNetworkProvidersForMarketAnalysis: state,
      },
    });
  }

  private handleMarketChange = (ps: ProviderSet) => {
    const providerSet = ps && ps.resource ? ps : null;

    this.setState((prevState) => {
      let newUseMarketProvidersForImpact = false;
      if (!!providerSet && !!prevState) {
        if (!!prevState.createProjectModel.marketProviderSetId) {
          newUseMarketProvidersForImpact = prevState.createProjectModel.useMarketProvidersForImpact;
        } else {
          newUseMarketProvidersForImpact = true;
        }
      } // else it stays false

      return {
        ...prevState,
        createProjectModel: {
          ...prevState.createProjectModel,
          marketProviderDescription: providerSet ? providerSet.resource.name : null,
          marketProviderSetId: providerSet ? providerSet.resource.resourceId : null,
          useMarketProvidersForImpact: newUseMarketProvidersForImpact,
        },
      }
    });
  }

  /*
   * Step 5: Network Taxonomy
   */
  private handleNetworkTaxonomyChange = (networkTaxonomyData: NetworkTaxonomyData, isValid: boolean) => {
    this.setState({
      createProjectModel: {
        ...this.state.createProjectModel,
        networkTaxonomy: networkTaxonomyData,
      },
      isNetworkTaxonomyValid: isValid
    })
  }

  /*
   *  Step 6: Select Region Set
   */
  private handleRegionSetChange = (rs: RegionSet) => {
    this.setState({
      createProjectModel: {
        ...this.state.createProjectModel,
        serviceAreaRegionSetId: rs.regionSetId,
      },
      serviceAreaRegionSet: rs,
    });
  }

  private registerNearbyCountiesTriggerClose = (triggerClose: () => void) => {
    this.setState({
      triggerClose,
      updateCounties: [],
    });
  }

  private updateLookupRegionSet = (lookupRegionSet: LookupRegionSet) => {
    this.setState({
      lookupRegionSet,
    });
  }

  private handleCountyStatusUpdates = async (changingStatuses: CountyStatus[]) => {
    const { createProjectModel } = this.state;

    this.setState({
      createProjectModel: {
        ...createProjectModel,
        countyStatuses: this.getUpdatedCountyStatuses(changingStatuses),
      },
    });
  }

  private getUpdatedCountyStatuses = (changingStatuses: CountyStatus[]) => {
    const { createProjectModel } = this.state;
    const updates: CountyStatus[] = createProjectModel.countyStatuses;

    for (const change of changingStatuses) {
      const indexToReplace = updates.findIndex((item) => item.countySSA === change.countySSA);
      if (indexToReplace >= 0) {
        if (change.status === RegionSetStatusType.Unknown) {
          updates.splice(indexToReplace, 1);
        } else {
          updates[indexToReplace] = change;
        }
      } else {
        if (change.status !== RegionSetStatusType.Unknown) {
          updates.push(change);
        }
      }
    }

    return updates;
  }

  private handleSubmitNearbyCounties = (updateCounties: County[], regionStatus: RegionSetStatusType) => {
    const { createProjectModel } = this.state;
    const changingStatuses = updateCounties.map((county) =>
    ({
      countySSA: county.countySSA,
      countyZips: [],
      status: regionStatus,
    } as CountyStatus),
    );

    this.setState({
      createProjectModel: {
        ...createProjectModel,
        countyStatuses: this.getUpdatedCountyStatuses(changingStatuses),
      },
      updateCounties,
    });
  }

  private handleCountyStatusForCheckedState = async (counties: County[]) => {
    const countiesToUpdate: CountyStatus[] = [];
    counties.map((county) => {
      const currentCounty = this.state.createProjectModel.countyStatuses.find((item) => item.countySSA === county.countySSA);
      if (!currentCounty || (currentCounty && currentCounty.status === RegionSetStatusType.Unknown)) {
        countiesToUpdate.push({
          countySSA: county.countySSA,
          countyZips: [],
          regionSetId: null,
          status: RegionSetStatusType.Active,
        });
      }

    });
    this.handleCountyStatusUpdates(countiesToUpdate);
  }

  /*
   * Step 7: Options
   */
  private handleOptionUpdate = (update: UpdateResourceKeys) => {
    const newProjectModel: CreateProject = {
      ...this.state.createProjectModel,
      [update]: !this.state.createProjectModel[update],
    };

    // if showPciData is being changed, turn off showPciDashboard, showLocationConfidenceScores, and showMotiveAppropriatenessScore, else keep current state
    if (update === "showPciData") {
      newProjectModel.showVaClaimsVolumeColumn = false;
      newProjectModel.showTricareClaimsVolumeColumn = false;
      newProjectModel.showLocationConfidenceScores = false;
      newProjectModel.showMotiveAppropriatenessScore = false;
      newProjectModel.showPciDashboard = false;
      newProjectModel.showCareJourneyScores = false;
    }

    // if showNextClosestNetworkProviders or hasZipSummaries is being changed, turn enableRegulatoryExceptionReports and showPatternOfCareProviders off if it was true
    if ((update === "showNextClosestNetworkProviders" || update === "hasZipSummaries") && (!!newProjectModel.enableRegulatoryExceptionReports || !!newProjectModel.showPatternOfCareProviders)) {
      newProjectModel.enableRegulatoryExceptionReports = false;
      newProjectModel.showPatternOfCareProviders = false;
    }

    this.setState({
      createProjectModel: newProjectModel,
    });
  }

  private updateExclusions = async () => {
    const showUseInMarketAnalysis = this.props.user.roles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER);
    const showEnterpriseComplianceDashboard = this.props.user.roles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER);
    const showCareJourneyScores = this.props.enabledFeatureFlags.has(Features.ENABLE_CARE_JOURNEY_SCORES);

    const newNetworkExclusions: string[] = [];
    if (this.state.createProjectModel.excludeDeactivatedFacilitiesNetwork) {
      newNetworkExclusions.push("DeactivatedFacility");
    }
    if (this.state.createProjectModel.excludeDeactivatedProvidersNetwork) {
      newNetworkExclusions.push("DeactivatedProvider");
    }
    if (this.state.createProjectModel.excludeInvalidNppesNpiFacilitiesNetwork) {
      newNetworkExclusions.push("InvalidNppesNpiFacility");
    }
    if (this.state.createProjectModel.excludeInvalidNppesNpiProvidersNetwork) {
      newNetworkExclusions.push("InvalidNppesNpiProvider");
    }
    if (this.state.createProjectModel.excludeMissingNpiFacilitiesNetwork) {
      newNetworkExclusions.push("MissingNpiFacility");
    }
    if (this.state.createProjectModel.excludeMissingNpiProvidersNetwork) {
      newNetworkExclusions.push("MissingNpiProvider");
    }
    if (this.state.createProjectModel.excludeMedicareOptOutProvidersNetwork) {
      newNetworkExclusions.push("MedicareOptOut");
    }
    if (this.state.createProjectModel.excludeOIGExcludedProvidersNetwork) {
      newNetworkExclusions.push("OIGExclusion");
    }
    if (this.state.createProjectModel.excludePrecludedProvidersNetwork) {
      newNetworkExclusions.push("Precluded");
    }

    if (newNetworkExclusions.length === 0) {
      newNetworkExclusions.push("None");
    }

    const newImpactExclusions: string[] = [];
    if (this.state.createProjectModel.excludeDeactivatedFacilitiesImpact) {
      newImpactExclusions.push("DeactivatedFacility");
    }
    if (this.state.createProjectModel.excludeDeactivatedProvidersImpact) {
      newImpactExclusions.push("DeactivatedProvider");
    }
    if (this.state.createProjectModel.excludeInvalidNppesNpiFacilitiesImpact) {
      newImpactExclusions.push("InvalidNppesNpiFacility");
    }
    if (this.state.createProjectModel.excludeInvalidNppesNpiProvidersImpact) {
      newImpactExclusions.push("InvalidNppesNpiProvider");
    }
    if (this.state.createProjectModel.excludeMissingNpiFacilitiesImpact) {
      newImpactExclusions.push("MissingNpiFacility");
    }
    if (this.state.createProjectModel.excludeMissingNpiProvidersImpact) {
      newImpactExclusions.push("MissingNpiProvider");
    }
    if (this.state.createProjectModel.excludeMedicareOptOutProvidersImpact) {
      newImpactExclusions.push("MedicareOptOut");
    }
    if (this.state.createProjectModel.excludeOIGExcludedProvidersImpact) {
      newImpactExclusions.push("OIGExclusion");
    }
    if (this.state.createProjectModel.excludePrecludedProvidersImpact) {
      newImpactExclusions.push("Precluded");
    }

    if (newImpactExclusions.length === 0) {
      newImpactExclusions.push("None");
    }

    const newProjectModel: CreateProject = {
      ...this.state.createProjectModel,
      impactExclusions: newImpactExclusions,
      networkExclusions: newNetworkExclusions,
    };
    this.setState({
      createProjectModel: newProjectModel,
      hideCareJourneyScores: !showCareJourneyScores,
      hideEnterpriseComplianceDashboard: !showEnterpriseComplianceDashboard,
      hideShowInMarketAnalysis: !showUseInMarketAnalysis,
    });
  }
}

const WrappedCreateProjectWizard = withUserContext(withFeatureFlagContext(CreateProjectWizard));

export default WrappedCreateProjectWizard as React.ComponentClass<UserContextProps & RouteComponentProps, CreateProjectWizardState>;
